// Feed.jsx - 完整修复版
import React, { useEffect, useRef, useState, useCallback } from "react"
import { Box, Skeleton, Typography, CircularProgress, Snackbar } from "@mui/material"
import { useVirtualizer } from "@tanstack/react-virtual"
import FeedCardList from "./feedCardList"
import { DOMAIN } from "../../config"
import SearchDialog from "../appDialog"
import AppInfo from "../common/appInfo"
import VideoPlayerDialog from "../videoPlayerCard"
import { normalizeUrl } from "../../funcs"

export default function Feed({
  handleScroll = null,
  matches,
  latest,
  loading,
  title = null,
  remark = true,
  setLatest = null,
  fetchUrl = `${DOMAIN}ywyminiappbot/getLatest`,
  reset = false
}) {
  const containerRef = useRef()

  // 播放器相关
  const [openPlayer, setOpenPlayer] = useState(false)
  const [player, setPlayer] = useState("")
  const [agent, setAgent] = useState("")
  const [user, setUser] = useState(null)
  const [playerVideoId, setPlayerVideoId] = useState("")
  const [playerThumbnail, setPlayerThumbnail] = useState("")
  const [playerUrl, setPlayerUrl] = useState("")
  const [playerTitle, setPlayerTitle] = useState("")

  const handleChangeVideoid = (newId, thumbnail) => {
    setOpenPlayer(false)
    setTimeout(() => {
      setPlayerVideoId(newId)
      setPlayerThumbnail(thumbnail)
      setOpenPlayer(true)
    }, 300)
  }

  // 搜索相关
  const [openSearch, setOpenSearch] = useState(false)
  const [chipTitle, setChipTitle] = useState(null)

  // AppInfo
  const [appInfoOpen, setAppInfoOpen] = useState(false)
  const [appInfoMessage, setAppInfoMessage] = useState("收藏视频成功")

  // Snackbar 提示
  const [snackbarOpen, setSnackbarOpen] = useState(false)

  // 加载更多状态
  const [loadingMore, setLoadingMore] = useState(false)

  // 播放器初始化
  useEffect(() => {
    fetch(`${DOMAIN}ywyminiappbot/getPlayer`)
      .then(res => res.json())
      .then(data => setPlayer(data[0]?.url || ""))

    window.Telegram?.WebApp?.ready?.()
    const tel_user = window.Telegram?.WebApp?.initDataUnsafe
    if (tel_user) {
      setUser(tel_user)
      const start_param = tel_user.start_param
      const urlParams = new URLSearchParams(start_param || "")
      if (urlParams.get("agent")) setAgent(`-agent_${urlParams.get("agent")}`)
    }
  }, [])

  // 虚拟滚动
  const rowVirtualizer = useVirtualizer({
    count: latest.length + 1,
    getScrollElement: () => containerRef.current,
    estimateSize: useCallback(
      (index) => {
        if (index === latest.length) return 60
        const video = latest[index]
        if (!video) return 500
        let size = video.medias.length > 2 ? 545 : 430
        const caption = video.caption || ""
        const lines = caption.split("\n")
        if (!remark) size -= 30
        if (lines.length === 1 && caption.length < 100) size -= 20
        return size
      },
      [latest, remark]
    ),
    overscan: 8,
  })

  // 页码与滚动锁
  const [page, setPage] = useState(1)
  const [finished, setFinished] = useState(false)
  const scrollLock = useRef(false)
  const lastRequestedPage = useRef(0)

  // 保持最新数据引用
  const latestRef = useRef(latest)
  useEffect(() => {
    latestRef.current = latest
  }, [latest])

  // 加载更多函数
  const loadMore = useCallback(async () => {
    
    if (scrollLock.current || finished) return
   
    const nextPage = page + 1
    
    if (nextPage <= lastRequestedPage.current) return

    scrollLock.current = true
    setLoadingMore(true)

    try {
      const connector = fetchUrl.includes("?") ? "&" : "?"
      const response = await fetch(normalizeUrl(`${fetchUrl}${connector}page=${nextPage}`))
      const data = await response.json()

      if (!data || data.length === 0) {
        setFinished(true)
        setSnackbarOpen(true)
      } else {
        setLatest(prev => {
          const next = [...(prev || []), ...data]
          latestRef.current = next
          if (next.length > 800) return next.slice(-500) // iOS 内存优化
          return next
        })
        setPage(nextPage)
        lastRequestedPage.current = nextPage
      }
    } catch (err) {
      console.error("加载更多失败:", err)
    } finally {
      scrollLock.current = false
      setLoadingMore(false)
    }
  }, [page, finished, fetchUrl, setLatest])

  // 初始加载第一页（防止 iOS 回收重复加载）
  useEffect(() => {
    if (!latest || latest.length === 0) {
      loadMore()
    }
  }, []) // eslint-disable-line

  // 滚动监听
  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    let ticking = false
    const onScroll = () => {
      if (ticking) return
      ticking = true

      window.requestAnimationFrame(() => {
        const bottom = el.scrollHeight - el.scrollTop - el.clientHeight
        if (bottom < 200 && !scrollLock.current && !finished) {
          
          loadMore()
        }
        if (handleScroll) handleScroll(el)
        ticking = false
      })
    }

    el.addEventListener("scroll", onScroll)
    return () => el.removeEventListener("scroll", onScroll)
  }, [loadMore, finished, handleScroll])

  // 过滤器 reset
  const [reloadKey, setReloadKey] = useState(0)
  useEffect(() => {
    
    if (!reset || !reset.startsWith("filter")) return

    setLatest([])
    setFinished(false)
    setPage(0)
    lastRequestedPage.current = 0
    
    setReloadKey(k => k + 1)
  }, [reset])

  useEffect(() => {
    
    if (reloadKey > 0) loadMore()
  }, [reloadKey])

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflowY: "auto",
        width: "100%",
        height: "100vh",
        backgroundColor: "#fafafa",
        pt: "50px",
      }}
    >
      {/* AppInfo */}
      <AppInfo
        message={appInfoMessage}
        vertical="bottom"
        setAppInfoOpen={setAppInfoOpen}
        appInfoOpen={appInfoOpen}
      />

      {/* 搜索框 */}
      <SearchDialog
        openSearch={openSearch}
        setOpenSearch={setOpenSearch}
        matches={matches}
        chipTitle={chipTitle}
      />

      {/* 视频播放器 */}
      <VideoPlayerDialog
        open={openPlayer}
        onClose={() => setOpenPlayer(false)}
        videoid={playerVideoId}
        thumbnail={playerThumbnail}
        matches={matches}
        setOpenPlayer={setOpenPlayer}
        handleChangeVideoid={handleChangeVideoid}
        player={player}
      />

      {/* 虚拟滚动容器 */}
      <Box sx={{ position: "relative", height: `${rowVirtualizer.getTotalSize()}px` }}>
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          if (virtualRow.index === latest.length) {
            return finished ? (
              <Box
                key="finished"
                sx={{
                  position: "absolute",
                  top: virtualRow.start,
                  width: "100%",
                  textAlign: "center",
                  py: 2,
                  color: "#888",
                }}
              >
                <Typography variant="body2" fontWeight={400} fontSize={13}>
                  已经是最后一条了～
                </Typography>
              </Box>
            ) : null
          }

          const video = latest[virtualRow.index]
          if (!video) return null

          return (
            <Box
              key={video.id ?? `video-${virtualRow.index}`}
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${virtualRow.start}px)`,
                mb: 3,
                px: { xs: 0, sm: 1, md: 2 },
              }}
            >
              <FeedCardList
                matches={matches}
                video={video}
                setLatest={setLatest}
                title={title}
                chipTitle={chipTitle}
                remark={remark}
                player={player}
                agent={agent}
                user={user}
                setAppInfoOpen={setAppInfoOpen}
                setAppInfoMessage={setAppInfoMessage}
                handleScroll={handleScroll}
                openPlayerGlobal={setOpenPlayer}
                setPlayerUrl={setPlayerUrl}
                setPlayerTitle={setPlayerTitle}
                setPlayerThumbnail={setPlayerThumbnail}
                setPlayerVideoId={setPlayerVideoId}
                setOpenSearch={setOpenSearch}
                setChipTitle={setChipTitle}
              />
            </Box>
          )
        })}
      </Box>

      {/* Skeleton */}
      {loading && latest.length === 0 &&
        Array.from({ length: 3 }).map((_, i) => (
          <Box key={i} sx={{ margin: 1 }}>
            <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 2 }} />
            <Skeleton variant="text" />
            <Skeleton variant="text" width="80%" />
          </Box>
        ))}

      {/* 加载中圆圈 */}
      <Box
        sx={{
          position: "fixed",
          bottom: 16,
          left: 16,
          zIndex: 1000,
          display: loadingMore ? "flex" : "none",
          alignItems: "center",
          justifyContent: "center",
          width: 36,
          height: 36,
          bgcolor: "rgba(255,255,255,0.9)",
          borderRadius: "50%",
          boxShadow: 3,
        }}
      >
        <CircularProgress size={24} />
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message="已经是最后一条数据了"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        ContentProps={{
          sx: {
            bgcolor: "rgba(0,0,0,0.7)",
            color: "#fff",
            fontSize: 14,
          },
        }}
      />
    </Box>
  )
}
